import cn from 'classnames';
import React, { useMemo } from 'react';

import { ReactComponent as LoadingIcon } from 'assets/loader.svg';
import { ReactComponent as PauseIcon } from 'assets/pause.svg';
import { ReactComponent as PlayIcon } from 'assets/play.svg';
import { ReactComponent as ReplayIcon } from 'assets/replay.svg';
import styles from './PlaybackIcon.module.scss';

type PlaybackStatus = 'paused' | 'playing' | 'ended';
type IconName = 'loading' | 'pause' | 'play' | 'replay';

export interface PlaybackIconProps {
  className?: string;
  loading?: boolean;
  status: PlaybackStatus;
}

const ICONS: Record<IconName, React.ComponentType<{ className?: string }>> = {
  loading: LoadingIcon,
  pause: PauseIcon,
  play: PlayIcon,
  replay: ReplayIcon,
};

const PlaybackIcon: React.FC<PlaybackIconProps> = ({
  className,
  loading,
  status,
}) => {
  const iconType: IconName = useMemo(() => {
    if (status === 'playing' && loading) {
      return 'loading';
    }

    if (status === 'playing') {
      return 'pause';
    }

    if (status === 'paused') {
      return 'play';
    }

    return 'replay';
  }, [loading, status]);

  const getIconClassNames = (suffix: IconName) => {
    const internalClassNames = cn(
      styles.iconContainer,
      styles[`iconContainer__${suffix}`],
    );
    const propsClassNames = cn(className, {
      [`${className}__${suffix}`]: !!className,
    });
    return cn(internalClassNames, propsClassNames);
  };

  const IconComponent = ICONS[iconType];

  return (
    // icon needs to be wrapped in a container and the container needs to be
    // animated, if an animation is required.  this is because of a Safari issue
    // where scaling and rotating the spinner icon results in distortion of the
    // spinner icon
    <div className={getIconClassNames(iconType)}>
      <IconComponent className={styles.icon} />
    </div>
  );
};

export default PlaybackIcon;
